/**
 * CompliantOrb — Sprint A1
 *
 * The compressed green orb showing all compliant measures.
 * - Pulses once (1.5s) when freshCount increments during live encounter
 * - Shows ⁺ badge for 30s after last fresh capture
 * - Click → dropdown listing all compliant measures
 * - Dropdown sorted: captured-today first, then expiring-soon, then stable
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { CheckCircle2, ChevronDown, ChevronUp } from 'lucide-react';
import { QualityDropdownRow } from './QualityDropdownRow';

export function CompliantOrb({ count, freshCount = 0, isLiveEncounter = false, chips = [] }) {
  const [pulsing, setPulsing] = useState(false);
  const [showPlus, setShowPlus] = useState(false);
  const [open, setOpen] = useState(false);
  const prevFreshRef = useRef(freshCount);
  const plusTimerRef = useRef(null);
  const dropdownRef = useRef(null);

  // Pulse + ⁺ badge on fresh capture (only during live encounter)
  useEffect(() => {
    if (!isLiveEncounter) return;
    if (freshCount > prevFreshRef.current) {
      // Fire pulse
      setPulsing(true);
      const t = setTimeout(() => setPulsing(false), 1500);
      // Show ⁺ badge for 30s, resetting timer on each new capture
      setShowPlus(true);
      if (plusTimerRef.current) clearTimeout(plusTimerRef.current);
      plusTimerRef.current = setTimeout(() => setShowPlus(false), 30000);
      prevFreshRef.current = freshCount;
      return () => clearTimeout(t);
    }
  }, [freshCount, isLiveEncounter]);

  // Cleanup timers
  useEffect(() => () => { if (plusTimerRef.current) clearTimeout(plusTimerRef.current); }, []);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  if (count === 0) return null;

  // Sort dropdown: captured-today first, then expiring-soon, then stable
  const sortedChips = [...chips].sort((a, b) => {
    const aToday = a.captured_this_encounter ? 0 : 1;
    const bToday = b.captured_this_encounter ? 0 : 1;
    if (aToday !== bToday) return aToday - bToday;
    const aCrit = a.expiration?.is_critically_soon ? 0 : a.expiration?.is_expiring_soon ? 1 : 2;
    const bCrit = b.expiration?.is_critically_soon ? 0 : b.expiration?.is_expiring_soon ? 1 : 2;
    return aCrit - bCrit;
  });

  return (
    <div ref={dropdownRef} className="relative" data-testid="compliant-orb">
      {/* The orb button */}
      <button
        onClick={() => setOpen(o => !o)}
        className={`
          inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border
          text-[10px] font-semibold select-none transition-colors
          bg-emerald-100 border-emerald-300 text-emerald-800
          hover:bg-emerald-200 hover:shadow-sm
          ${pulsing ? 'quality-orb-pulsing' : ''}
        `}
        data-testid="compliant-orb-btn"
        aria-expanded={open}
        aria-label={`${count} compliant measures — click to expand`}>
        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 shrink-0" />
        <CheckCircle2 className="w-3 h-3 text-emerald-600" />
        <span>{count}</span>
        {/* ⁺ badge — appears after fresh capture */}
        {showPlus && (
          <sup className="text-emerald-600 font-bold" style={{ fontSize: '8px', lineHeight: 1 }}>+</sup>
        )}
        {open ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
      </button>

      {/* Dropdown */}
      {open && (
        <div
          className="absolute left-0 top-full mt-1.5 w-80 bg-card rounded-xl border shadow-2xl z-50 overflow-hidden animate-in fade-in-0 slide-in-from-top-2 duration-150"
          data-testid="compliant-orb-dropdown"
          style={{ maxHeight: '440px', overflowY: 'auto' }}>
          <div className="px-3 py-2 border-b bg-emerald-50/50 flex items-center gap-1.5">
            <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
            <span className="text-[10px] font-semibold text-emerald-700 uppercase tracking-wider">
              {count} Compliant Measure{count !== 1 ? 's' : ''}
            </span>
          </div>
          <div>
            {sortedChips.map(chip => (
              <QualityDropdownRow
                key={chip.measure_id}
                chip={chip}
                showActions={false}
              />
            ))}
          </div>
        </div>
      )}

      {/* Pulse animation — injected via style tag to avoid CSS module dependency */}
      <style>{`
        @keyframes quality-orb-pulse {
          0%   { opacity: 1;    transform: scale(1);    box-shadow: 0 0 0px rgba(16,185,129,0); }
          50%  { opacity: 0.65; transform: scale(1.06); box-shadow: 0 0 8px rgba(16,185,129,0.4); }
          100% { opacity: 1;    transform: scale(1);    box-shadow: 0 0 0px rgba(16,185,129,0); }
        }
        .quality-orb-pulsing {
          animation: quality-orb-pulse 1.5s ease-in-out 1 forwards;
        }
      `}</style>
    </div>
  );
}
